import { createAsyncThunk } from "@reduxjs/toolkit";
import { WhiteboardActionTypes, WhiteboardItem, WhiteboardResponse, WhiteboardItemResponse, WhiteboardOrderUpdate } from "./types";
import api from "../../../utils/constants/api";
import { apiEndpoints } from "../../../utils/constants/apiEndpoints";
import { ApiResponse } from "../../../types/APIResponse";
import { convertUtcToLocal } from "../../../utils/helpers/convertUtcToLocal";

// Async thunks
export const fetchWhiteboardItems = createAsyncThunk(WhiteboardActionTypes.FETCH_WHITEBOARD_ITEMS, async ({ page, pageSize }: { page: number; pageSize: number }, { rejectWithValue }) => {
  try {
    const offset = (page - 1) * pageSize;
    const response = await api.post<WhiteboardResponse>(apiEndpoints.whiteboard.getAll, {
      data: {
        offset,
        limit: pageSize,
        order: { "whiteboard.index": "DESC" },
      },
    });

    if (!response.success) {
      throw new Error(response.message || "Failed to fetch whiteboard items");
    }

    // Process the data to match our UI requirements
    const processedItems = response.data.map((item) => ({
      ...item,
      addedBy: item.admin ? `${item.admin.firstName} ${item.admin.lastName}` : "Unknown",
      date: item.createdAt ? convertUtcToLocal(item.createdAt, "MM/DD/YYYY hh:mm A") : "",
    }));

    return {
      items: processedItems,
      totalCount: response.counts,
    };
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to fetch whiteboard items");
  }
});

export const addWhiteboardItem = createAsyncThunk(WhiteboardActionTypes.ADD_WHITEBOARD_ITEM, async (message: string, { rejectWithValue }) => {
  try {
    const response = await api.post<WhiteboardItemResponse>(apiEndpoints.whiteboard.create, {
      data: { message },
    });

    if (!response.success) {
      throw new Error(response.message || "Failed to add whiteboard item");
    }

    // Process the data to match our UI requirements
    const item = response.data;
    return {
      ...item,
      addedBy: item.admin ? `${item.admin.firstName} ${item.admin.lastName}` : "Unknown",
      date: item.createdAt ? convertUtcToLocal(item.createdAt, "MM/DD/YYYY hh:mm A") : "",
    };
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to add whiteboard item");
  }
});

export const updateWhiteboardItem = createAsyncThunk(WhiteboardActionTypes.UPDATE_WHITEBOARD_ITEM, async ({ id, message }: { id: string; message: string }, { rejectWithValue }) => {
  try {
    const response = await api.put<WhiteboardItemResponse>(`${apiEndpoints.whiteboard.update}/${id}`, {
      data: { message },
    });

    if (!response.success) {
      throw new Error(response.message || "Failed to update whiteboard item");
    }

    // Process the data to match our UI requirements
    const item = response.data;
    return {
      ...item,
      addedBy: item.admin ? `${item.admin.firstName} ${item.admin.lastName}` : "Unknown",
      date: item.createdAt ? convertUtcToLocal(item.createdAt, "MM/DD/YYYY hh:mm A") : "",
    };
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to update whiteboard item");
  }
});

export const deleteWhiteboardItem = createAsyncThunk(WhiteboardActionTypes.DELETE_WHITEBOARD_ITEM, async (id: string, { rejectWithValue }) => {
  try {
    const response = await api.delete<ApiResponse<null>>(`${apiEndpoints.whiteboard.delete}/${id}`);

    if (!response.success) {
      throw new Error(response.message || "Failed to delete whiteboard item");
    }

    return id;
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to delete whiteboard item");
  }
});

export const moveWhiteboardItemUp = createAsyncThunk(WhiteboardActionTypes.MOVE_WHITEBOARD_ITEM_UP, async (id: string, { getState, rejectWithValue }) => {
  try {
    const state = getState() as { whiteboard: { items: WhiteboardItem[] } };
    const items = state.whiteboard.items;
    const currentIndex = items.findIndex((item) => item.id === id);

    if (currentIndex <= 0) {
      // Already at the top of the displayed list, nothing to do
      return id;
    }

    // Since we're displaying in DESC order, moving "up" in the UI means
    // swapping with the item that has a higher index value in the database
    // (the item that appears above it in the UI has a lower index in the array)
    const orderUpdates: WhiteboardOrderUpdate[] = [
      { id: items[currentIndex].id, index: items[currentIndex - 1].index },
      { id: items[currentIndex - 1].id, index: items[currentIndex].index },
    ];

    const response = await api.put<ApiResponse<null>>(apiEndpoints.whiteboard.updateOrder, {
      data: { orderUpdates },
    });

    if (!response.success) {
      throw new Error(response.message || "Failed to move whiteboard item up");
    }

    return id;
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to move whiteboard item up");
  }
});

export const moveWhiteboardItemDown = createAsyncThunk(WhiteboardActionTypes.MOVE_WHITEBOARD_ITEM_DOWN, async (id: string, { getState, rejectWithValue }) => {
  try {
    const state = getState() as { whiteboard: { items: WhiteboardItem[] } };
    const items = state.whiteboard.items;
    const currentIndex = items.findIndex((item) => item.id === id);

    if (currentIndex === -1 || currentIndex >= items.length - 1) {
      // Already at the bottom of the displayed list or not found, nothing to do
      return id;
    }

    // Since we're displaying in DESC order, moving "down" in the UI means
    // swapping with the item that has a lower index value in the database
    // (the item that appears below it in the UI has a higher index in the array)
    const orderUpdates: WhiteboardOrderUpdate[] = [
      { id: items[currentIndex].id, index: items[currentIndex + 1].index },
      { id: items[currentIndex + 1].id, index: items[currentIndex].index },
    ];

    const response = await api.put<ApiResponse<null>>(apiEndpoints.whiteboard.updateOrder, {
      data: { orderUpdates },
    });

    if (!response.success) {
      throw new Error(response.message || "Failed to move whiteboard item down");
    }

    return id;
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to move whiteboard item down");
  }
});
